import { useState, useEffect, useRef } from 'react';
import { authService } from '../../services/api';
import { useNotification } from '../Notifications/NotificationContext';
import BotIcon from '../Icons/BotIcon';

// Cache de módulo: botId → photo_url (evita fetch repetido na mesma sessão)
const photoUrlCache = new Map();

export default function BotProfileModal({ open, bot, onClose, onUpdated }) {
    const { showError, showSuccess } = useNotification();

    const [botName, setBotName] = useState('');
    const [description, setDescription] = useState('');
    const [bio, setBio] = useState('');
    const [photoUrl, setPhotoUrl] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [pendingPhoto, setPendingPhoto] = useState(null);
    const [loadingPhoto, setLoadingPhoto] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);
    const [loadingSync, setLoadingSync] = useState(false);
    const fileRef = useRef();

    useEffect(() => {
        if (!open || !bot) return;
        setBotName(bot.bot_name || '');
        setDescription(bot.bot_description || '');
        setBio(bot.bot_bio || '');
        setPhotoPreview(null);
        setPendingPhoto(null);
        fetchPhoto();
    }, [open, bot?.id]);

    const fetchPhoto = async () => {
        if (!bot?.id) return;
        if (photoUrlCache.has(bot.id)) {
            setPhotoUrl(photoUrlCache.get(bot.id));
            return;
        }
        if (!bot.bot_photo_file_id) {
            setPhotoUrl(null);
            return;
        }
        setLoadingPhoto(true);
        try {
            const { data } = await authService.bots.get_bot_photo(bot.id);
            const url = data.photo_url || null;
            photoUrlCache.set(bot.id, url);
            setPhotoUrl(url);
        } catch {
            setPhotoUrl(null);
        } finally {
            setLoadingPhoto(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            showError('Selecione uma imagem válida.');
            return;
        }
        setPendingPhoto(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    const handleSync = async () => {
        setLoadingSync(true);
        try {
            const { data } = await authService.bots.sync_bot_profile(bot.id);
            if (!data.success) throw new Error(data.error);
            setBotName(data.bot_name || '');
            setDescription(data.bot_description || '');
            setBio(data.bot_bio || '');
            photoUrlCache.delete(bot.id);
            setPhotoUrl(null);
            setPhotoPreview(null);
            // Bypass the stale bot.bot_photo_file_id prop check — use the fresh sync result
            if (data.bot_photo_file_id) {
                setLoadingPhoto(true);
                try {
                    const { data: pd } = await authService.bots.get_bot_photo(bot.id);
                    const url = pd.photo_url || null;
                    if (url) photoUrlCache.set(bot.id, url);
                    setPhotoUrl(url);
                } catch { /* ignore */ } finally {
                    setLoadingPhoto(false);
                }
            }
            showSuccess('Perfil sincronizado com o Telegram!');
            onUpdated?.(data);
        } catch (err) {
            showError(err?.response?.data?.error || err.message);
        } finally {
            setLoadingSync(false);
        }
    };

    const handleSave = async () => {
        setLoadingSave(true);
        try {
            const { data } = await authService.bots.update_bot_profile(bot.id, {
                bot_name: botName,
                bot_description: description,
                bot_bio: bio,
            });
            if (!data.success) throw new Error(data.error);

            if (pendingPhoto) {
                const fd = new FormData();
                fd.append('photo', pendingPhoto);
                const { data: pd } = await authService.bots.update_bot_photo(bot.id, fd);
                if (!pd.success) throw new Error(pd.error);
                photoUrlCache.delete(bot.id);
                setPhotoPreview(null);
                setPendingPhoto(null);
                await fetchPhoto();
            }

            showSuccess('Perfil do bot atualizado!');
            onUpdated?.({ bot_name: botName, bot_description: description, bot_bio: bio });
            onClose();
        } catch (err) {
            showError(err?.response?.data?.error || err.message);
        } finally {
            setLoadingSave(false);
        }
    };

    const handleDeletePhoto = async () => {
        if (!window.confirm('Remover foto de perfil do bot?')) return;
        try {
            await authService.bots.delete_bot_photo(bot.id);
            photoUrlCache.delete(bot.id);
            setPhotoUrl(null);
            setPhotoPreview(null);
            setPendingPhoto(null);
            showSuccess('Foto removida.');
            onUpdated?.({ bot_photo_file_id: null });
        } catch (err) {
            showError(err?.response?.data?.error || err.message);
        }
    };

    if (!open) return null;

    const displayPhoto = photoPreview || photoUrl;

    return (
        <div className="manage-bots-modal-overlay" onClick={onClose}>
            <div className="manage-bots-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="manage-bots-modal-header">
                    <h2 className="manage-bots-modal-title">Perfil do Bot</h2>
                    <button className="manage-bots-modal-close" onClick={onClose}>×</button>
                </div>

                {/* Foto */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                    <div
                        style={{
                            width: 72, height: 72, borderRadius: '50%',
                            background: 'var(--bg-secondary, #1e1e2e)',
                            border: '2px solid rgba(139,92,246,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            overflow: 'hidden', flexShrink: 0,
                        }}
                    >
                        {loadingPhoto ? (
                            <i className="fas fa-spinner fa-spin" style={{ color: 'var(--text-secondary)' }} />
                        ) : displayPhoto ? (
                            <img src={displayPhoto} alt="foto do bot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <BotIcon size={30} color="var(--text-secondary)" />
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {/* <button className="spt-btn spt-btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }}
                            onClick={() => fileRef.current?.click()}>
                            <i className="fas fa-upload" style={{ marginRight: 6 }} />
                            {displayPhoto ? 'Trocar foto' : 'Adicionar foto'}
                        </button>
                        {(photoUrl || photoPreview) && (
                            <button className="spt-btn" style={{ fontSize: 12, padding: '4px 12px', background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}
                                onClick={photoPreview ? () => { setPhotoPreview(null); setPendingPhoto(null); } : handleDeletePhoto}>
                                <i className="fas fa-trash" style={{ marginRight: 6 }} />
                                {photoPreview ? 'Cancelar' : 'Remover foto'}
                            </button>
                        )}
                        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} /> */}
                        <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
                            Adicione a Foto pelo BotFather para que ela seja exibida aqui. A sincronização busca a foto atual do Telegram, caso tenha sido alterada por lá.
                        </span>
                    </div>
                </div>

                {/* Campos */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                        <label className="spt-label">Nome do Bot</label>
                        <input
                            className="input3"
                            value={botName}
                            onChange={e => setBotName(e.target.value)}
                            maxLength={64}
                            placeholder="Nome exibido no Telegram"
                        />
                    </div>
                    <div>
                        <label className="spt-label">Descrição</label>
                        <textarea
                            className="input3"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            maxLength={512}
                            rows={3}
                            placeholder="Descrição longa exibida no perfil do bot"
                            style={{ resize: 'vertical' }}
                        />
                    </div>
                    <div>
                        <label className="spt-label">About (bio curta)</label>
                        <textarea
                            className="input3"
                            value={bio}
                            onChange={e => setBio(e.target.value)}
                            maxLength={120}
                            rows={2}
                            placeholder="Texto curto exibido na lista de contatos"
                            style={{ resize: 'vertical' }}
                        />
                    </div>
                </div>

                {/* Footer */}

                <button
                    className="btn-primary w-full mt-4"
                    onClick={handleSync}
                    disabled={loadingSync}
                    title="Busca nome, foto e descrição atuais do BotFather"
                    style={{ fontSize: 13 }}
                >
                    <i className={`fas fa-sync${loadingSync ? ' fa-spin' : ''}`} style={{ marginRight: 6 }} />
                    Sincronizar com Telegram
                </button>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 32, gap: 8 }}>

                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn-secondary" onClick={onClose}>Cancelar</button>
                        <button className="btn-primary" onClick={handleSave} disabled={loadingSave}>
                            {loadingSave ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-save" /> Salvar</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
